import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, CalendarRange, Loader2 } from "lucide-react";
import AppLayout from "@/app/AppLayout";
import { LGoButton } from "@/ui/branded";
import { PageHeader, EmptyState, AsyncBoundary } from "@/ui/patterns";
import { useUserBusiness } from "@/features/settings/hooks/useUserBusiness";
import { NoBusinessYet } from "@/features/settings/components/NoBusinessYet";
import { useCanTrade } from "@/features/settings/hooks/usePaymentAccount";
import {
  TradingPendingNotice,
  TradingStatusUnknownNotice,
} from "@/features/offers/components/TradingPendingNotice";
import { useOffers } from "@/features/offers/hooks/useOffers";
import { usePartnerOffers } from "@/features/offers/hooks/usePartnerOffers";
import { OfferList } from "@/features/offers/components/OfferList";
import { useReservationList } from "@/features/reservations/hooks/useReservationList";
import { tallyOfferSales, unknownSales, type OfferSalesTally } from "@/domain/offer/rules";
import { salesRefusalMessage } from "@/features/offers/sales-locks";
import { czechPlural, offerForms } from "@/lib/czech";
import { OfferFormDialog } from "@/features/offers/components/OfferFormDialog";
import { OfferSeriesDialog } from "@/features/offers/components/OfferSeriesDialog";
import { CancelOfferDialog } from "@/features/offers/components/CancelOfferDialog";
import type { OfferDraft } from "@/features/offers/hooks/useOfferForm";
import type { PartnerOffer } from "@/domain/offer/types";

/** Same name and shape as `/reservations`' offer filter — one vocabulary for
 *  "this screen, that offer" across the portal. */
function offerIdFrom(params: URLSearchParams): number | null {
  const asked = params.get("offer");
  if (asked === null || !/^\d+$/.test(asked)) return null;
  return Number(asked);
}

function seriesRefusalTitle(
  hasActivities: boolean,
  havePublishedOffersLoaded: boolean,
): string | undefined {
  if (!hasActivities) return "Nejprve vytvořte šablonu slev";
  if (!havePublishedOffersLoaded) return "Počkejte, než načteme už vypsané termíny";
  return undefined;
}

export default function OffersPage() {
  const {
    businessId,
    isLoading: isLoadingBusiness,
    error: businessError,
    refetch: refetchBusiness,
  } = useUserBusiness();
  const {
    canTrade,
    isLoading: isLoadingTrading,
    error: tradingError,
    refetch: refetchTrading,
  } = useCanTrade();
  const { activities, createOffer, createOffers, updateOffer } = useOffers();
  const { offers, isLoading, error, refetch, cancelOffer } = usePartnerOffers();
  const { reservations, hasLoaded: hasLoadedReservations } = useReservationList();
  const [searchParams] = useSearchParams();
  const highlightedOfferId = offerIdFrom(searchParams);

  const [formOpen, setFormOpen] = useState(false);
  const [seriesOpen, setSeriesOpen] = useState(false);
  const [editedOffer, setEditedOffer] = useState<PartnerOffer | null>(null);
  const [cancelledOffer, setCancelledOffer] = useState<PartnerOffer | null>(null);

  // A reservations request still in flight or failed has counted nothing, so
  // the editor is told it does not know rather than handed a zero.
  const editedSales = useMemo<OfferSalesTally>(() => {
    if (!editedOffer) return { sold: 0 };
    if (!hasLoadedReservations) return unknownSales;
    return tallyOfferSales(reservations, editedOffer.id);
  }, [hasLoadedReservations, reservations, editedOffer]);

  const handleCreate = () => {
    setEditedOffer(null);
    setFormOpen(true);
  };

  const handleEdit = (offer: PartnerOffer) => {
    setEditedOffer(offer);
    setFormOpen(true);
  };

  const handleSubmit = async (draft: OfferDraft) => {
    try {
      if (editedOffer) {
        await updateOffer.mutateAsync({ offer: editedOffer, draft });
        toast.success("Slevový termín byl upraven");
      } else {
        await createOffer.mutateAsync(draft);
        toast.success("Slevový termín byl vytvořen");
      }
      setFormOpen(false);
    } catch (error) {
      const refusal = salesRefusalMessage(error);
      if (refusal) {
        toast.error(refusal);
        return;
      }
      console.error("Chyba při ukládání slevového termínu:", error);
      toast.error("Chyba při ukládání slevového termínu");
    }
  };

  const handleSubmitSeries = async (drafts: OfferDraft[]) => {
    const { published, failed } = await createOffers.mutateAsync(drafts);
    if (failed.length === 0) {
      toast.success(`Vypsali jsme ${published} ${czechPlural(published, offerForms)}.`);
      setSeriesOpen(false);
      return failed;
    }
    toast.error(
      published === 0
        ? `Nevypsali jsme ani jeden termín. Všech ${failed.length} jsme nechali v přehledu — zkuste to prosím znovu.`
        : `Vypsali jsme ${published} ${czechPlural(published, offerForms)}. Zbytek (${failed.length}) jsme nechali v přehledu — zkuste ho vypsat znovu.`,
    );
    return failed;
  };

  const handleCancelOffer = () => {
    if (!cancelledOffer) return;
    cancelOffer.mutate(cancelledOffer.id, { onSettled: () => setCancelledOffer(null) });
  };

  if (isLoadingBusiness || isLoadingTrading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
        </div>
      </AppLayout>
    );
  }

  if (businessError || businessId === null) {
    return (
      <AppLayout>
        <AsyncBoundary<boolean>
          data={businessId !== null}
          isLoading={false}
          error={businessError}
          onRetry={refetchBusiness}
        >
          {() => (
            <NoBusinessYet
              eyebrow="Slevové termíny"
              headline="Bez podniku to nezačne."
              body="Slevový termín odkazuje na konkrétní podnik a šablonu slev — nejprve nastavte podnik, pak založíme šablony slev a teprve potom termíny."
            />
          )}
        </AsyncBoundary>
      </AppLayout>
    );
  }

  const hasActivities = activities.length > 0;
  // The generator refuses termíny colliding with published ones, and a list
  // that has not arrived collides with nothing — so it may not run on one.
  const havePublishedOffersLoaded = !isLoading && !error;
  const canGenerateSeries = hasActivities && havePublishedOffersLoaded;

  return (
    <AppLayout>
      <div className="space-y-8">
        <PageHeader
          title="Slevové termíny"
          actions={
            <>
              <LGoButton
                variant="secondary"
                onClick={() => setSeriesOpen(true)}
                disabled={!canGenerateSeries}
                size="lg"
                iconLeft={<CalendarRange />}
                title={seriesRefusalTitle(hasActivities, havePublishedOffersLoaded)}
              >
                Více termínů
              </LGoButton>
              <LGoButton
                onClick={handleCreate}
                disabled={!hasActivities}
                size="lg"
                iconLeft={<Plus />}
                title={!hasActivities ? "Nejprve vytvořte šablonu slev" : undefined}
              >
                Nový slevový termín
              </LGoButton>
            </>
          }
        />

        <AsyncBoundary
          data={canTrade}
          isLoading={false}
          error={tradingError}
          onRetry={refetchTrading}
          errorFallback={(_error, retry) => <TradingStatusUnknownNotice onRetry={retry} />}
        >
          {(trades) => (trades ? null : <TradingPendingNotice />)}
        </AsyncBoundary>

        {!hasActivities ? (
          <div className="border border-dashed border-border rounded-xl max-w-2xl mx-auto">
            <EmptyState
              variant="hero"
              title="Šablona slev musí jít první."
              description="Slevový termín vypisujete z konkrétní šablony slev (např. „Lekce jógy“). Nejdřív přidejte šablony slev, pak z nich můžete kdykoliv vypsat termín se slevou."
              action={
                <LGoButton asChild size="lg">
                  <Link to="/activities">Přejít na šablony slev</Link>
                </LGoButton>
              }
            />
          </div>
        ) : (
          <AsyncBoundary<PartnerOffer[]>
            data={offers}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
          >
            {(loadedOffers) => (
              <OfferList
                offers={loadedOffers}
                canTrade={canTrade}
                highlightedOfferId={highlightedOfferId}
                onEdit={handleEdit}
                onCancel={setCancelledOffer}
              />
            )}
          </AsyncBoundary>
        )}
      </div>

      <OfferFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        offer={editedOffer}
        activities={activities}
        sales={editedSales}
        reservedSeats={editedOffer?.reserved ?? 0}
        onSubmit={handleSubmit}
        isLoading={createOffer.isPending || updateOffer.isPending}
      />

      <OfferSeriesDialog
        open={seriesOpen}
        onOpenChange={setSeriesOpen}
        activities={activities}
        publishedOffers={offers}
        havePublishedOffersLoaded={havePublishedOffersLoaded}
        onSubmit={handleSubmitSeries}
        isLoading={createOffers.isPending}
      />

      <CancelOfferDialog
        offer={cancelledOffer}
        onOpenChange={(open) => !open && setCancelledOffer(null)}
        onConfirm={handleCancelOffer}
        isCancelling={cancelOffer.isPending}
      />
    </AppLayout>
  );
}
